/**
 * utmCapture.js (iter251, 2026-02)
 *
 * Captures UTM parameters from the landing URL on first page load and
 * persists them in sessionStorage so they survive the tutorial +
 * registration flow. On signup, `getPersistedUtm()` returns them so the
 * backend can save them on the user document.
 *
 * Why sessionStorage (not localStorage)
 * --------------------------------------
 * The UTM attribution window is "this browsing session". If the user
 * closes the tab and comes back organically next week we DO NOT want
 * to attribute that signup to a Meta campaign — we'd be lying to
 * ourselves. For 90-day attribution we have Meta's own `fbclid` →
 * `_fbc` cookie (handled in `metaPixel.js`), which is purpose-built.
 *
 * Behaviour
 * ---------
 *   1. Call `captureUtmParams()` once at app bootstrap (before React).
 *   2. If the URL has at least one `utm_*` param → store ALL present
 *      params as a single JSON blob under `rvincente_utm`.
 *   3. Never overwrite an existing capture (first-touch attribution).
 *   4. `getPersistedUtm()` returns the stored object or `null`.
 */

const SS_KEY = 'rvincente_utm';
const FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

export function captureUtmParams() {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
  try {
    // First-touch: never overwrite. If the user re-opens the same tab
    // and lands on an organic page we must not reset the attribution.
    if (sessionStorage.getItem(SS_KEY)) return;
    const url = new URL(window.location.href);
    const captured = {};
    for (const f of FIELDS) {
      const v = url.searchParams.get(f);
      if (v) captured[f] = v.slice(0, 128); // mirror backend cap
    }
    if (Object.keys(captured).length === 0) return;
    sessionStorage.setItem(SS_KEY, JSON.stringify(captured));
  } catch (e) {
    // Never let a tracking error break the UX.
    // eslint-disable-next-line no-console
    console.warn('[utmCapture] failed:', e);
  }
}

// iter274 — Android Play Install Referrer UTM (survives app restarts until signup).
const INSTALL_KEY = 'rvincente_utm_install';

export function persistInstallUtm(params) {
  try {
    const captured = {};
    for (const f of FIELDS) if (params.get(f)) captured[f] = params.get(f).slice(0, 128);
    if (Object.keys(captured).length && !localStorage.getItem(INSTALL_KEY)) {
      localStorage.setItem(INSTALL_KEY, JSON.stringify(captured));
    }
  } catch { /* ignore */ }
}

/** iter280 — UTM + where they came from + device platform, sent at signup. */
export function getSignupAttribution() {
  const out = { ...(getPersistedUtm() || {}) };
  try {
    let fromLink = false;
    try { fromLink = !!sessionStorage.getItem(SS_KEY); } catch { /* ignore */ }
    if (Object.keys(out).length) out.utm_origin = fromLink ? 'link' : 'install_referrer';
    const cap = window.Capacitor;
    const p = cap && typeof cap.getPlatform === 'function' ? cap.getPlatform() : 'web';
    out.signup_platform = ['android', 'ios'].includes(p) ? p : 'web';
  } catch { /* ignore */ }
  return out;
}

export function getPersistedUtm() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    let raw = sessionStorage.getItem(SS_KEY);
    try { raw = raw || localStorage.getItem(INSTALL_KEY); } catch { /* ignore */ }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Shallow-sanitize: only return known fields, no extras.
    const clean = {};
    for (const f of FIELDS) if (parsed[f]) clean[f] = String(parsed[f]).slice(0, 128);
    return Object.keys(clean).length ? clean : null;
  } catch {
    return null;
  }
}
